import type { NextApiRequest, NextApiResponse } from 'next';
import { withAuth } from '../../../lib/apiAuth';
import { getSupabaseAdminClient } from '../../../lib/supabaseAdmin';
import { classifyDocument } from '../../../lib/documents/classifier';

/**
 * POST /api/documents/split-pdf
 *
 * Body: { document_id: string }
 *
 * Returns: { success, original_pages, documents_created, splits: Array<{ pages, tipo? }> }
 */
async function handler(
  req: NextApiRequest,
  res: NextApiResponse,
  user: { id: string; email: string; roles: string[]; primaryRole: string }
) {
  if (req.method !== 'POST') {
    return res.status(405).json({ success: false, error: 'Method not allowed' });
  }

  const supabaseAdmin = getSupabaseAdminClient();

  try {
    const { document_id } = req.body;

    if (!document_id) {
      return res.status(400).json({ success: false, error: 'document_id es requerido.' });
    }

    const { data: doc, error: docError } = await supabaseAdmin
      .from('documentos')
      .select('id, empresa_id, nombre, url_archivo, tipo')
      .eq('id', document_id)
      .single();

    if (docError || !doc) {
      return res.status(404).json({ success: false, error: 'Documento no encontrado.' });
    }

    if (!doc.nombre.toLowerCase().endsWith('.pdf')) {
      return res.status(400).json({
        success: false,
        error: 'Solo se pueden dividir documentos PDF.',
      });
    }

    const storagePath = doc.url_archivo.replace(
      /^https:\/\/[^/]+\/storage\/v1\/object\/public\/documentos\//,
      ''
    );

    if (!storagePath || storagePath === doc.url_archivo) {
      return res.status(500).json({
        success: false,
        error: 'No se pudo determinar la ruta de almacenamiento del documento.',
      });
    }

    const { data: fileData, error: fileError } = await supabaseAdmin.storage
      .from('documentos')
      .download(storagePath);

    if (fileError || !fileData) {
      return res.status(500).json({
        success: false,
        error: 'Error descargando el PDF desde el almacenamiento.',
      });
    }

    const pdfBuffer = Buffer.from(await fileData.arrayBuffer());

    interface SplitPage {
      pages: number[];
      text: string;
    }

    let splitPages: SplitPage[] = [];
    let totalPages = 0;

    try {
      const { splitPdfByDocuments } = await import('../../../lib/documents/pdf-splitter');
      const advancedResult = await splitPdfByDocuments(pdfBuffer);
      totalPages = advancedResult.reduce((max, g) => Math.max(max, ...g.pages), 0);
      splitPages = advancedResult.map((g) => ({ pages: g.pages, text: g.text }));
    } catch {
      try {
        const { splitPdfByPages } = await import('../../../lib/documents/pdf-splitter');
        const basicResult = await splitPdfByPages(pdfBuffer);
        totalPages = basicResult.length;
        splitPages = basicResult.map((r) => ({
          pages: [r.pageNumber],
          text: r.text,
        }));
      } catch (parseErr: unknown) {
        const msg = parseErr instanceof Error ? parseErr.message : 'Error al parsear el PDF';
        return res.status(500).json({ success: false, error: msg });
      }
    }

    if (totalPages <= 1) {
      return res.status(200).json({
        success: true,
        original_pages: totalPages,
        documents_created: 0,
        splits: [],
      });
    }

    const baseName = doc.nombre.replace(/\.pdf$/i, '');
    const splits: Array<{ pages: number[]; tipo?: string }> = [];
    let documentsCreated = 0;

    for (const split of splitPages) {
      const pageLabel = split.pages.length === 1
        ? `pagina_${split.pages[0]}`
        : `paginas_${split.pages[0]}-${split.pages[split.pages.length - 1]}`;
      const splitName = `${baseName}_${pageLabel}.pdf`;

      const classification = classifyDocument(splitName, 'application/pdf', split.text);

      const { error: insertError } = await supabaseAdmin
        .from('documentos')
        .insert({
          nombre: splitName,
          url_archivo: doc.url_archivo,
          tipo: classification.tipo,
          empresa_id: doc.empresa_id,
          estado: 'pendiente',
          documento_padre_id: document_id,
        });

      if (insertError) {
        console.error(
          `[split-pdf] Error creating split for pages ${split.pages.join(',')}:`,
          insertError.message
        );
        continue;
      }

      splits.push({
        pages: split.pages,
        tipo: classification.tipo,
      });
      documentsCreated++;
    }

    return res.status(200).json({
      success: true,
      original_pages: totalPages,
      documents_created: documentsCreated,
      splits,
    });
  } catch (error: any) {
    console.error('[split-pdf] Error:', error);
    return res.status(500).json({
      success: false,
      error: error.message || 'Error al dividir el PDF.',
    });
  }
}

export default withAuth(handler, ['admin', 'asesor']);
